import { cx, api, rx } from "../../../../api";
import { ofType, combineEpics } from 'redux-observable';
import { switchMap, takeUntil, map as rxmap, filter, tap, ignoreElements } from 'rxjs/operators';
import { formatDateTime, getTimezoneString } from '../../../../misc/misc';
import { resolver } from './resolver';
import { actions } from '../actions';
import { f, fc } from "../../../../../i18n";
import { errorMap } from "../../../actions";
import { getAssetType } from "../../../../misc/assetTypes";

const requestEpic = (action$) => {
	return action$.pipe(
		ofType(actions.idling.request.type),
		switchMap(action =>
			rx(api.reports.idling, action.uri, action.parameters).pipe(
				rxmap(operation => actions.idling.success({ intervals: operation.response() })),
				errorMap(actions.idling.fail),
				takeUntil(action$.pipe(ofType(actions.idling.cancel.type)))
			)
		)
	)
}

const startPrepareEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.idling.export.type),
		rxmap(action => {
			const state = state$.value.reports.idling;
			resolver.resolve(state.list, !state.hasMore);
			if (state.hasMore) {
				return actions.idling.request({ uri: action.uri, parameters: state.parameters });
			} else {
				return actions.idling.success({});
			}
		})
	)
}

const processPrepareEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.idling.success.type),
		filter(action => {
			const state = state$.value.reports.idling;
			if (state.exporting) resolver.resolve(action.intervals, !state.hasMore);
			return state.exporting && state.hasMore;
		}),
		rxmap(action => {
			const state = state$.value.reports.idling;
			return actions.idling.request({ uri: state.uri, parameters: state.parameters });
		})
	)
}

const exportCsvEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.idling.exportProgress.type),
		filter(action => action.progress == 100),
		rxmap(action => {
			const state = state$.value.reports.idling;
			const device = state$.value.devices.map[state.uri];
			const isGroup = !!state.parameters.groupingId;
			const isAssetType = !!state.parameters.isAssetTypes;

			let group = '-';

			if (isGroup && device.categoryIds) {
				const categoryMap = state$.value.categories.general.map;

				device.categoryIds.forEach(categoryId => {
					const category = categoryMap[categoryId];
					const comprisingIds = category?.comprisingIds;

					if (comprisingIds && state.parameters.groupingId && comprisingIds.includes(state.parameters.groupingId)) {
						group = category.name;
					}
				});
			}

			const assetType = isAssetType ? getAssetType(device.categoryIds)?.getName() || '-' : null;

			// report header
			let csv = '"'
        + fc('report type')
        + '","'
        + fc('generated')
        + '","'
        + fc('timezone')
        + '","'
        + fc('device')
        + '","'
        + fc('since')
        + '","'
        + fc('until')
        + '","'
        + fc('maximum speed') + ', ' + f('units.km/h')
        + '","'
        + fc('minimum period')
        + '"\n';
			csv += '"' + fc('idling') + '"';  // report type
			csv += ',"' + formatDateTime(cx.now()) + '"'; // generated at
			csv += ',"' + getTimezoneString() + '"'; // timezone at
			csv += ',"' + (device.denomination() || device.uri) + '"';
			csv += ',"' + formatDateTime(state.parameters.timeRange.since) + '"';
			csv += ',"' + formatDateTime(state.parameters.timeRange.until) + '"';
			csv += ',"' + state.parameters.maxSpeed + '"';
			csv += ',"' + state.parameters.minPeriod + '"';
			csv += "\n\n";

			// content header
			let contentHeader = '';

			// Add Group and Asset Type columns if needed
			if (isGroup) {
				contentHeader += '"' + fc('group') + '",';
			}

			if (isAssetType) {
				contentHeader += '"' + fc('asset type') + '",';
			}

			// Add Since column
			contentHeader += '"' + fc('since') + '"';

			// Add remaining columns
			contentHeader += ',"'
        +  fc('since location')
        + '","'
        + fc('since address')
        + '","'
        + fc('until')
        + '","'
        + fc('until location')
        + '","'
        + fc('until address')
        + '"';

			// Add empty columns to ensure we have 8 columns total
			const columnsAdded = (isGroup ? 1 : 0) + (isAssetType ? 1 : 0);
			const emptyColumnsNeeded = 2 - columnsAdded;
			for (let i = 0; i < emptyColumnsNeeded; i++) {
				contentHeader += ',""';
			}

			csv += contentHeader + '\n';

			// content
			state.list.forEach(interval => {
				let row = '';

				// Add Group and Asset Type values if needed
				if (isGroup) {
					row += '"' + group + '",';
				}

				if (isAssetType) {
					row += '"' + (assetType || '-') + '",';
				}

				// since
				const sinceMessage = interval.since;
				row += '"' + formatDateTime(sinceMessage.generatedAt) + '"';
				row += ',"(' + sinceMessage.latitude + ';' + sinceMessage.longitude + ')"';
				let entryKey = resolver.key(sinceMessage.latitude, sinceMessage.longitude);
				let address = resolver.resolved[entryKey]?.getAddress();
				if (address) row += ',"' + address.format() + '"';
				else row += ',"' + '-' + '"';

				// until
				const untilMessage = interval.until;
				if (!untilMessage) row += ',"","",""';
				else {
					row += ',"' + formatDateTime(untilMessage.generatedAt) + '"';
					row += ',"(' + untilMessage.latitude + ';' + untilMessage.longitude + ')"';
					entryKey = resolver.key(untilMessage.latitude, untilMessage.longitude);
					address = resolver.resolved[entryKey]?.getAddress();
					if (address) row += ',"' + address.format() + '"';
					else row += ',"' + '-' + '"';
				}

				// Add empty columns to ensure we have 8 columns total
				for (let i = 0; i < emptyColumnsNeeded; i++) {
					row += ',""';
				}

				csv += row + '\n';
			});
			return actions.idling.exportDone({ csv });
		}),
	)
}

const exportClearEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.idling.exportClear.type),
		tap(() => resolver.clear()),
		ignoreElements()
	);
}

const epic = combineEpics(requestEpic, startPrepareEpic, processPrepareEpic, exportCsvEpic, exportClearEpic);

export { epic };
